import { addDays, format, parseISO, subDays } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { useEffect, useMemo, useRef, useState } from 'react'
import { data, useNavigate, useRevalidator } from 'react-router'
import {
	AddressLabels,
	addressLabelKey,
} from '#app/components/farm-map/address-labels.tsx'
import { FarmMap } from '#app/components/farm-map/farm-map.tsx'
import { WaterMarker } from '#app/components/farm-map/water-marker.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { prisma } from '#app/utils/db.server.ts'
import {
	DITCH_ORDER,
	buildDitchWindows,
	computeEntryStatus,
	getPortPoints,
} from '#app/utils/farm-map.ts'
import { getDateTimeFormat, formatHours } from '#app/utils/misc.tsx'
import { SearchResultsSchema } from '#app/utils/user-schedule.ts'
import { type Route } from './+types/map.ts'

const scheduleQuery = (scheduleId: string) => prisma.$queryRaw`
    SELECT User.id AS userId, User.display,
		   Port.id AS portId, Port.ditch, Port.position, Port.entry, Port.section, Port.address, Port.direction,
           UserSchedule.hours, UserSchedule.start, UserSchedule.stop
      FROM User
     INNER JOIN Port ON User.id = Port.userId
      LEFT JOIN UserSchedule
        ON User.id = UserSchedule.userId
       AND Port.id = UserSchedule.portId
       AND UserSchedule.scheduleId = ${scheduleId}
     WHERE User.active
     ORDER BY Port.ditch, Port.position
  `

const liveQuery = (yesterday: Date, tomorrow: Date) => prisma.$queryRaw`
    SELECT User.id AS userId, User.display,
		   Port.id AS portId, Port.ditch, Port.position, Port.entry, Port.section, Port.address, Port.direction,
           UserSchedule.hours, UserSchedule.start, UserSchedule.stop
      FROM User
     INNER JOIN Port ON User.id = Port.userId
      LEFT JOIN UserSchedule
        ON User.id = UserSchedule.userId
       AND Port.id = UserSchedule.portId
     WHERE User.active
       AND UserSchedule.start >= ${yesterday}
       AND UserSchedule.start <= ${tomorrow}
     ORDER BY UserSchedule.start ASC
  `

export async function loader({ params, request }: Route.LoaderArgs) {
	const url = new URL(request.url)
	const time = getDateTimeFormat(request).format(new Date())
	const inputDateValue = params.date ?? url.searchParams.get('date')
	const inputDate = inputDateValue ? parseISO(inputDateValue) : undefined
	const date = format(inputDate ?? new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
	const scheduleDate = format(inputDate ?? new Date(), 'yyyy-MM-dd')

	const schedule = await prisma.schedule.findFirst({
		select: { id: true, start: true, stop: true },
		where: { date: { lte: scheduleDate } },
		orderBy: { date: 'desc' },
	})

	const ports = await prisma.port.findMany({
		select: {
			ditch: true,
			position: true,
			address: true,
			entry: true,
			direction: true,
		},
		where: { user: { active: true }, entry: { not: null } },
	})

	if (schedule) {
		const rawRows = await scheduleQuery(schedule.id)
		const result = SearchResultsSchema.safeParse(rawRows)
		const rows = result.success ? result.data : []

		const starts = rows.flatMap((row) =>
			row.start ? [row.start.getTime()] : [],
		)
		const stops = rows.flatMap((row) => (row.stop ? [row.stop.getTime()] : []))
		const scheduleStart =
			schedule.start ??
			new Date(starts.length ? Math.min(...starts) : Date.now())
		const scheduleStop =
			schedule.stop ??
			new Date(stops.length ? Math.max(...stops) : Date.now() + 86_400_000)

		return data({
			status: 'idle',
			mode: 'schedule',
			date,
			rows,
			ports,
			inputDate,
			time,
			scheduleStart,
			scheduleStop,
		} as const)
	}

	const yesterday = subDays(new Date(), 1)
	const tomorrow = addDays(new Date(), 1)
	const rawRows = await liveQuery(yesterday, tomorrow)
	const result = SearchResultsSchema.safeParse(rawRows)
	const rows = result.success ? result.data : []

	return data({
		status: 'idle',
		mode: 'live',
		date,
		rows,
		ports,
		inputDate,
		time,
		scheduleStart: yesterday,
		scheduleStop: tomorrow,
	} as const)
}

const colors: Record<string, string> = {
	'10-01': 'fill-sky-500 stroke-sky-900',
	'10-03': 'fill-cyan-500 stroke-cyan-900',
}

const PLAYBACK_TICK_MS = 20
const PLAYBACK_STEP_MS = 15 * 60 * 1000
const PRE_ROLL_MS = 15 * 60 * 1000

/** Compact "in 2h 30m" / "45m ago" / "now" label for a signed millisecond offset. */
function formatRelativeTime(deltaMs: number): string {
	const minutes = Math.round(Math.abs(deltaMs) / 60000)
	if (minutes < 1) return 'now'
	const days = Math.floor(minutes / 1440)
	const hours = Math.floor((minutes % 1440) / 60)
	const mins = minutes % 60
	const parts = [
		days ? `${days}d` : '',
		hours ? `${hours}h` : '',
		!days && mins ? `${mins}m` : '',
	].filter(Boolean)
	const label = parts.join(' ')
	return deltaMs > 0 ? `in ${label}` : `${label} ago`
}

/** Describes a lot's state vs real `now`: finished / running / upcoming. */
function lotTimingLabel(
	startMs: number | null,
	stopMs: number | null,
	nowMs: number,
): string | null {
	if (stopMs !== null && stopMs <= nowMs)
		return `Finished ${formatRelativeTime(stopMs - nowMs)}`
	if (startMs !== null && startMs <= nowMs)
		return `Started ${formatRelativeTime(startMs - nowMs)}`
	if (startMs !== null) return `Starts ${formatRelativeTime(startMs - nowMs)}`
	if (stopMs !== null) return `Finishes ${formatRelativeTime(stopMs - nowMs)}`
	return null
}

export default function MapRoute({ loaderData }: Route.ComponentProps) {
	const { status, mode, date, rows, ports, scheduleStart, scheduleStop, time } =
		loaderData
	const inputDate = loaderData.inputDate
	const navigate = useNavigate()
	const revalidator = useRevalidator()

	const scheduleStartMs = new Date(scheduleStart).getTime()
	const scheduleStopMs = new Date(scheduleStop).getTime()
	const timelineStartMs =
		mode === 'schedule' ? scheduleStartMs - PRE_ROLL_MS : scheduleStartMs
	const inputDateMs = inputDate ? new Date(inputDate).getTime() : undefined
	const requestedTimeMs =
		inputDateMs ??
		(mode === 'schedule' ? Date.parse(`${time} UTC`) : Date.now())
	// real "now" in the schedule's instant space, independent of the slider position
	const nowMs = mode === 'schedule' ? Date.parse(`${time} UTC`) : Date.now()
	const requestedTimelineTime = Math.min(
		scheduleStopMs,
		Math.max(timelineStartMs, requestedTimeMs),
	)
	const [sliderTime, setSliderTime] = useState(() => requestedTimelineTime)
	const [playing, setPlaying] = useState(false)
	const [playbackStopTime, setPlaybackStopTime] = useState(
		requestedTimelineTime,
	)
	// live = viewing the current schedule at "now" without any manual interaction
	const [isLive, setIsLive] = useState(
		() => !inputDate && Date.now() < scheduleStopMs,
	)
	const playRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const portPoints = getPortPoints(ports)
	const atInputDate =
		inputDateMs !== undefined && sliderTime === requestedTimelineTime
	const atScheduleEnd = sliderTime >= scheduleStopMs

	useEffect(() => {
		setSliderTime(requestedTimelineTime)
		setPlaying(false)
		setPlaybackStopTime(requestedTimelineTime)
		setIsLive(!inputDate && Date.now() < scheduleStopMs)
	}, [date, requestedTimelineTime, inputDate, scheduleStopMs])

	useEffect(() => {
		if (!isLive) return
		let timeoutId: ReturnType<typeof setTimeout>
		const tick = () => {
			setSliderTime(
				Math.min(scheduleStopMs, Math.max(timelineStartMs, Date.now())),
			)
			if (revalidator.state === 'idle') void revalidator.revalidate()
			if (Date.now() >= scheduleStopMs) setIsLive(false)
			scheduleNext()
		}
		// align to the next :00/:15/:30/:45 wall-clock boundary
		const scheduleNext = () => {
			const msUntilNextQuarter =
				PLAYBACK_STEP_MS - (Date.now() % PLAYBACK_STEP_MS)
			timeoutId = setTimeout(tick, msUntilNextQuarter)
		}
		scheduleNext()
		return () => clearTimeout(timeoutId)
	}, [isLive, revalidator, scheduleStopMs, timelineStartMs])

	useEffect(() => {
		if (!playing || playbackStopTime <= scheduleStartMs) {
			if (playRef.current) clearInterval(playRef.current)
			return
		}
		playRef.current = setInterval(() => {
			setSliderTime((current) => {
				const next = current + PLAYBACK_STEP_MS
				if (next >= playbackStopTime) {
					setPlaying(false)
					return playbackStopTime
				}
				return next
			})
		}, PLAYBACK_TICK_MS)
		return () => {
			if (playRef.current) clearInterval(playRef.current)
		}
	}, [playing, playbackStopTime, scheduleStartMs])

	const entryStatuses = useMemo(
		() =>
			Object.keys(DITCH_ORDER).map((entry) => {
				const order = DITCH_ORDER[entry] ?? []
				const windows = buildDitchWindows(rows, entry, order)
				const entryStop = Math.max(
					...rows
						.filter((row) => row.entry === entry && row.stop)
						.map((row) => row.stop!.getTime()),
					Number.NEGATIVE_INFINITY,
				)
				const computedStatus = computeEntryStatus(windows, order, sliderTime)
				const entryStatus =
					computedStatus.status !== 'idle' &&
					entryStop !== Number.NEGATIVE_INFINITY &&
					sliderTime >= entryStop
						? { ...computedStatus, status: 'complete' as const }
						: computedStatus
				const activeRow =
					entryStatus.ditch !== null
						? rows.find(
								(row) =>
									row.entry === entry &&
									row.ditch === entryStatus.ditch &&
									row.start &&
									row.stop &&
									row.start.getTime() <= sliderTime &&
									row.stop.getTime() >= sliderTime,
							)
						: undefined
				const activePortPoint = activeRow
					? portPoints.find(
							(port) =>
								port.ditch === activeRow.ditch &&
								port.entry === activeRow.entry &&
								port.position === activeRow.position &&
								port.address === activeRow.address,
						)
					: undefined
				const body =
					entryStatus.status === 'idle'
						? 'Not Started'
						: entryStatus.status === 'active'
							? `Irrigating: ${activeRow ? ` ${activeRow.display}${activeRow.address ? ` (${activeRow.address})` : ''}` : ''} `
							: entryStatus.status === 'complete'
								? 'Finished'
								: `Crossing over from Ditch ${entryStatus.ditch}`
				const label = `[${entry}]: ${body}`
				const entryStart = windows.length ? windows[0]!.start : null
				// selection follows the slider; the "ago/in" wording is anchored to real now
				const timing =
					entryStatus.status === 'active' && activeRow
						? lotTimingLabel(
								activeRow.start?.getTime() ?? null,
								activeRow.stop?.getTime() ?? null,
								nowMs,
							)
						: entryStatus.status === 'complete' &&
							  entryStop !== Number.NEGATIVE_INFINITY
							? lotTimingLabel(null, entryStop, nowMs)
							: entryStart !== null
								? lotTimingLabel(entryStart, null, nowMs)
								: null
				const nextRow = rows
					.filter(
						(row) =>
							row.entry === entry &&
							row.start &&
							row.start.getTime() > sliderTime,
					)
					.sort((a, b) => a.start!.getTime() - b.start!.getTime())[0]
				const upNext = nextRow
					? {
							display: nextRow.display,
							address: nextRow.address,
							ditch: nextRow.ditch,
							hours: nextRow.hours,
							startsIn: lotTimingLabel(
								nextRow.start!.getTime(),
								nextRow.stop?.getTime() ?? null,
								nowMs,
							),
						}
					: null
				const prevRow = rows
					.filter(
						(row) =>
							row.entry === entry &&
							row.stop &&
							row.stop.getTime() <= sliderTime,
					)
					.sort((a, b) => b.stop!.getTime() - a.stop!.getTime())[0]
				const previous = prevRow
					? {
							display: prevRow.display,
							address: prevRow.address,
							ditch: prevRow.ditch,
							hours: prevRow.hours,
							finishedAgo: lotTimingLabel(
								prevRow.start?.getTime() ?? null,
								prevRow.stop!.getTime(),
								nowMs,
							),
						}
					: null
				return {
					entry,
					...entryStatus,
					fraction: activePortPoint?.fraction ?? entryStatus.fraction,
					label,
					body,
					hours: activeRow?.hours ?? null,
					timing,
					upNext,
					previous,
				}
			}),
		[portPoints, rows, sliderTime, nowMs],
	)
	const visitedAddresses = new Set(
		rows
			.filter(
				(row) =>
					row.address !== null && row.stop && row.stop.getTime() <= sliderTime,
			)
			.map((row) => addressLabelKey(row)),
	)
	const scheduledAddresses = new Set(
		rows
			.filter(
				(row) =>
					row.address !== null && row.stop && row.stop.getTime() > sliderTime,
			)
			.map((row) => addressLabelKey(row)),
	)
	const hoursByAddress = rows.reduce((map, row) => {
		if (row.address === null || !row.hours) return map
		const key = addressLabelKey(row)
		map.set(key, (map.get(key) ?? 0) + row.hours)
		return map
	}, new Map<string, number>())
	const displayTime = Math.max(sliderTime, scheduleStartMs)
	const displayTimeLabel =
		mode === 'schedule'
			? formatInTimeZone(
					new Date(displayTime),
					'Etc/UTC',
					'eee, MMM dd, h:mm aaa',
				)
			: format(new Date(displayTime), 'eee, MMM dd, h:mm aaa')

	if (status !== 'idle') return null

	return (
		<div className="mx-auto flex max-w-6xl flex-col gap-6 p-4">
			<div className="container my-4 flex flex-col items-center justify-center gap-4">
				<h1 className="text-h1 text-center">
					Irrigation Map for Schedule <br />
					{format(new Date(scheduleStartMs), ' eee, MMM d - ')}
					{format(new Date(scheduleStopMs), ' eee, MMM d, yyyy')}
				</h1>
				<h2 className="text-h4 flex items-center">
					<Icon
						name="droplets"
						className="mx-1 h-8 w-8 p-1"
						aria-hidden="true"
					/>
					Where is the water?
					<Icon
						name="droplet"
						className="mx-1 h-8 w-8 p-1"
						aria-hidden="true"
					/>
				</h2>
			</div>

			<div className="flex flex-col gap-3 rounded-lg border p-4">
				<div className="flex flex-wrap items-center gap-3">
					<label className="flex items-center gap-2 text-sm font-medium">
						Date:
						<input
							type="date"
							className="border-input bg-background rounded-md border px-2 py-1"
							value={format(parseISO(date), 'yyyy-MM-dd')}
							onChange={(event) =>
								navigate(`/schedule/map?date=${event.target.value}`)
							}
						/>
					</label>
					{mode === 'schedule' ? (
						<div className="flex flex-wrap gap-2">
							<button
								type="button"
								onClick={() => {
									setIsLive(false)
									setPlaybackStopTime(requestedTimelineTime)
									setSliderTime(timelineStartMs)
									setPlaying(true)
								}}
								disabled={scheduleStopMs <= scheduleStartMs}
								className="bg-secondary text-secondary-foreground rounded-md px-3 py-1 text-sm font-medium disabled:opacity-50"
							>
								⏮ Replay
							</button>
							<button
								type="button"
								disabled={scheduleStopMs <= scheduleStartMs}
								onClick={() => {
									setIsLive(false)
									if (!playing) {
										setPlaybackStopTime(scheduleStopMs)
										if (atScheduleEnd) setSliderTime(timelineStartMs)
									}
									setPlaying((p) => !p)
								}}
								className="bg-primary text-primary-foreground rounded-md px-3 py-1 text-sm font-medium disabled:opacity-50"
							>
								{playing ? '⏸ Pause' : atInputDate ? '▶ Continue' : '▶ Play'}
							</button>
							<button
								type="button"
								onClick={() => {
									setPlaying(false)
									setIsLive(true)
									void navigate('/schedule/map')
								}}
								className="bg-secondary text-secondary-foreground rounded-md px-3 py-1 text-sm font-medium"
							>
								Current Time
							</button>
						</div>
					) : (
						<span className="text-muted-foreground text-sm">
							Live tracking (no schedule found for this date)
						</span>
					)}
					{isLive ? (
						<span className="ml-auto flex items-center gap-1.5 rounded-md bg-red-600/10 px-2 py-1 text-sm font-semibold text-red-700">
							<span className="relative flex h-2.5 w-2.5">
								<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
								<span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-600" />
							</span>
							LIVE
						</span>
					) : null}
				</div>
				<input
					type="range"
					min={timelineStartMs}
					max={scheduleStopMs}
					step={15 * 60 * 1000}
					value={sliderTime}
					onChange={(event) => {
						setIsLive(false)
						setPlaying(false)
						setSliderTime(Number(event.target.value))
					}}
					className="w-full"
				/>
				<div className="text-muted-foreground flex flex-col items-center gap-1 text-center text-xs sm:flex-row sm:justify-between">
					<span className="w-[300px] rounded-lg border p-3 whitespace-nowrap">
						<strong>Schedule Start:</strong>{' '}
						{format(scheduleStartMs, 'eee, MMM d, h:mmaaa')}
					</span>
					<div className="bg-secondary border-primary w-[300px] rounded-lg border p-3 text-center text-sm whitespace-nowrap">
						<strong>Viewing:</strong> {displayTimeLabel}
					</div>
					<span className="w-[300px] rounded-lg border p-3 whitespace-nowrap">
						<strong>Schedule Stop:</strong>{' '}
						{format(scheduleStopMs, 'eee, MMM d, h:mmaaa')}
					</span>
				</div>
			</div>

			<div className="flex flex-col gap-2 sm:flex-row sm:justify-around">
				{[entryStatuses?.[0], entryStatuses?.[1]].map((entryStatus) =>
					entryStatus ? (
						<div
							key={entryStatus.entry}
							className="flex w-[460px] flex-col gap-2 rounded-lg border p-4"
						>
							<div className="text-h5 text-center">
								[{entryStatus.entry}] · Ditch {entryStatus.ditch}
							</div>
							<div className="bg-muted-40 rounded-lg border border-dashed p-2 text-center text-sm">
								{entryStatus.previous ? (
									<>
										<span className="text-muted-foreground">Previous:</span>{' '}
										{entryStatus.previous.display}
										{entryStatus.previous.address
											? ` (${entryStatus.previous.address})`
											: ''}{' '}
										<span className="text-muted-foreground">
											· {formatHours(entryStatus.previous.hours)}
										</span>
										<span className="text-muted-foreground mt-0.5 block text-xs font-medium">
											{entryStatus.previous.finishedAgo}
										</span>
									</>
								) : (
									<span className="text-muted-foreground">
										Nothing finished yet
									</span>
								)}
							</div>
							<div className="rounded-lg border border-sky-200 bg-sky-100 p-3 text-center">
								{entryStatus.body}
								{entryStatus.hours ? (
									<span className="text-muted-foreground">
										{' '}
										· {formatHours(entryStatus.hours)}
									</span>
								) : null}
								{entryStatus.timing ? (
									<span className="text-muted-foreground mt-1 block text-xs">
										{entryStatus.timing}
									</span>
								) : null}
							</div>
							<div className="bg-muted-40 rounded-lg border border-dashed p-2 text-center text-sm">
								{entryStatus.upNext ? (
									<>
										<span className="text-muted-foreground">Up next:</span>{' '}
										{entryStatus.upNext.display}
										{entryStatus.upNext.address
											? ` (${entryStatus.upNext.address})`
											: ''}{' '}
										<span className="text-muted-foreground">
											· {formatHours(entryStatus.upNext.hours)}
										</span>
										<span className="mt-0.5 block text-xs font-medium text-sky-700">
											{entryStatus.upNext.startsIn}
										</span>
									</>
								) : (
									<span className="text-muted-foreground">
										No upcoming lots
									</span>
								)}
							</div>
						</div>
					) : null,
				)}
			</div>

			<div className="rounded-lg border p-4">
				<FarmMap>
					<AddressLabels
						ports={ports}
						visitedAddresses={visitedAddresses}
						scheduledAddresses={scheduledAddresses}
						hoursByAddress={hoursByAddress}
					/>
					{entryStatuses.map((entryStatus) =>
						entryStatus.ditch !== null ? (
							<WaterMarker
								key={`${entryStatus.entry}-${entryStatus.ditch}`}
								entry={entryStatus.entry}
								ditch={entryStatus.ditch}
								fraction={entryStatus.fraction}
								label={entryStatus.label}
								colorClassName={colors[entryStatus.entry]}
							/>
						) : null,
					)}
				</FarmMap>
			</div>

			<p className="text-muted-foreground text-sm">
				<strong>Note:</strong> The map is not to scale. There are empty lots
				that are not accounted for and there are some lots that are larger or
				smaller than they appear on the map. The map is intended to show the
				order of irrigation and approximate location of each lot along the
				ditch.
				<br />
				<br />
				<strong>
					This map is a schematic guide to irrigation order and address
					placement; it is not a surveyed or to-scale map.
				</strong>
			</p>
		</div>
	)
}
